/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { FunctionComponent, useEffect, useRef, useState } from 'react';

import { OVERLAY_ANIMATION_DURATION } from '../../config';
import { DeviceModel } from '../../core/device';
import { DimensionScaler } from '../../core/device/DimensionScaler';
import { InteractiveComponents, SeparatorTypes } from '../../core/enums';
import { CursorManager, KeyManager } from '../../core/navigation';
import { RootState, useTivoDispatch, useTivoSelector } from '../../core/tivo/tivo-redux-wrapper';
import { TOverlayOptions } from '../../core/types';
import { RecordingRestriction } from '../../core/usecases';
import { setOverlayHighlightVerticalLimitAction } from '../../store/overlay/overlayhighlight/overlayhighlight.actions';
import {
    overlayDisableGhostMode,
    overlayEnableGhostMode,
} from '../../store/overlay/overlayhighlight/overlayhighlight.reducers';
import { OverlayTypes } from '../../store/overlay/OverlayTypes';
import { overlayDialogSetCaretVisible } from '../../store/overlaydialog/overlaydialog.reducers';
import {
    DescriptionType,
    IDescriptionEntry,
    IOverlayDialog,
} from '../../store/overlaydialog/overlaydialogprops';
import { Caret, Mousetrap, RestrictionMessage } from '../common';
import { BrandingIcon, Button, PinEntryWidget, SelectOption, Seperator, TextView } from './common';
import SelectMarkButton from './common/Body/SelectMarkButton/SelectMarkButton';
import { TextViewClasses } from './common/TextView/TextViewClasses';
import { OverlayInteractiveLayer } from './OverlayInteractiveLayer';

import './OverlayDialog.scss';

const OverlayDialog: FunctionComponent = (): JSX.Element => {
    const dispatch = useTivoDispatch();
    const { verticalPosition, horizontalLimit } = useTivoSelector(
        (state: RootState) => state.overlayHighlight,
    );
    const {
        uiProperties: uiProps,
        isOverlayDialogActive,
    }: { uiProperties: IOverlayDialog; isOverlayDialogActive: boolean } = useTivoSelector(
        (state: RootState) => state.overlayDialog,
    );
    const activeComponent = useTivoSelector(
        (state: RootState) => state.interactiveLayer.activeComponent,
    );

    const showOverlay = useRef(false);
    const overlayAnimationComplete = useRef(false);
    const textViewRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());
    const overlayRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const userOptionContainerRef = useRef<HTMLDivElement>(null);
    const [showVerticalCaret, setShowVerticalCaret] = useState(CursorManager.isCursorAppeared());

    useEffect(() => {
        if (uiProps.options?.length) {
            dispatch(setOverlayHighlightVerticalLimitAction(uiProps.options.length));
        }
        return () => {
            dispatch(setOverlayHighlightVerticalLimitAction(0));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uiProps.options]);

    useEffect(() => {
        if (overlayRef.current) {
            if (uiProps.isLargeSizeOverlay) {
                overlayRef.current.classList.add('large-size-overlay');
            } else {
                overlayRef.current.classList.remove('large-size-overlay');
            }
        }
    }, [uiProps.isLargeSizeOverlay]);

    const onCursorStateChangeCallback = (state) => {
        setShowVerticalCaret(state);
        if (!state) dispatch(overlayDisableGhostMode());
        else dispatch(overlayEnableGhostMode());
    };

    useEffect(() => {
        const ref = CursorManager.subscribe(onCursorStateChangeCallback);
        return () => {
            CursorManager.unsubscribe(ref);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * setting overlayAnimationComplete flag based on overlay show/hide animation completion
     * This flag is used to determine if its safe to scroll the focused button into view immediately
     * or wait for the animation to complete before scrolling.
     */
    useEffect(() => {
        let timerId;
        let overlayElement;
        const handleAnimationEnd = (event) => {
            if (event.animationName === 'show-dialog') {
                overlayAnimationComplete.current = true;
            }
            if (event.animationName === 'hide-dialog') {
                overlayAnimationComplete.current = false;
            }
        };

        if (!isOverlayDialogActive) {
            overlayAnimationComplete.current = false;
            timerId = setTimeout(() => {
                showOverlay.current = false;
            }, OVERLAY_ANIMATION_DURATION);
        } else {
            showOverlay.current = true;
            clearTimeout(timerId);

            // Listen for animation completion
            overlayElement = overlayRef.current;
            if (overlayElement) {
                overlayElement.addEventListener('animationend', handleAnimationEnd);
            }

            if (KeyManager.getInstance().isKeyIgnoring())
                KeyManager.getInstance().stopKeyIgnoring();
        }

        return () => {
            clearTimeout(timerId);
            if (overlayElement) {
                overlayElement.removeEventListener('animationend', handleAnimationEnd);
            }
        };
    }, [isOverlayDialogActive]);

    useEffect(() => {
        if (uiProps.enableCaret && isOverlayDialogActive && horizontalLimit[verticalPosition] > 1) {
            dispatch(overlayDialogSetCaretVisible());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [CursorManager.isCursorAppeared()]);

    // subTitile division.
    const subtitileDiv = uiProps.subtitle ? (
        <TextView
            type={TextViewClasses.SUBTITLE}
            text={typeof uiProps.subtitle !== 'string' ? uiProps.subtitle.text : uiProps.subtitle}
            iconUrl={typeof uiProps.subtitle !== 'string' ? uiProps.subtitle.icon : ''}
        />
    ) : null;

    // brandingicon division.
    const brandingIconDiv = <BrandingIcon iconURL={uiProps.iconURL} />;

    // messageCode division.
    const messageCodeDiv = uiProps.messageCode ? (
        <TextView type={TextViewClasses.MESSAGE_CODE} text={uiProps.messageCode} />
    ) : null;

    const decorateDescription = (description: string | Array<IDescriptionEntry>) => {
        if (typeof description === 'string') {
            return (
                <TextView type={TextViewClasses.DESCRIPTION} text={uiProps.description as string} />
            );
        }
        return description.map((descriptionEntry, index) => {
            const descriptionEntryDiv: Array<JSX.Element> = [];
            if (descriptionEntry.hrTop)
                descriptionEntryDiv.push(<Seperator type={SeparatorTypes.DESCRIPTION} />);
            if (descriptionEntry.descriptionType === DescriptionType.FADED_DESCRIPTION)
                descriptionEntryDiv.push(
                    <TextView
                        type={TextViewClasses.FADED_DESCRIPTION}
                        text={descriptionEntry.text as string}
                    />,
                );
            else {
                descriptionEntryDiv.push(
                    <TextView
                        type={
                            descriptionEntry.descriptionType === DescriptionType.PROGRAM_DESCRIPTION
                                ? TextViewClasses.PROGRAM_DESCRIPTION
                                : TextViewClasses.DESCRIPTION
                        }
                        text={descriptionEntry.text}
                        ref={(el) => textViewRefs.current.set(index, el)}
                    />,
                );
            }
            if (descriptionEntry.hrBottom)
                descriptionEntryDiv.push(<Seperator type={SeparatorTypes.DESCRIPTION} />);
            return descriptionEntryDiv;
        });
    };

    // description division.
    const descriptionDiv = uiProps.description ? decorateDescription(uiProps.description) : null;

    const selectOptionsDiv = (index, option) => {
        const hasLeftMargin = option.marginLeft ?? false;

        return (
            <div key={index} className={hasLeftMargin ? 'left-margin' : ''}>
                {DeviceModel.isWebOsPlatform() && CursorManager.isCursorAppeared() ? (
                    <OverlayInteractiveLayer id={index} />
                ) : null}
                <SelectOption
                    options={option.horizontalSelectionoptions}
                    isFocused={index === verticalPosition}
                    text={`${option.text}:`}
                    id={index}
                    disabled={option.disabled}
                />
            </div>
        );
    };

    const selectMarkDiv = (index, option: TOverlayOptions, isLargeSizeOverlay: boolean) => {
        const hasLeftMargin = option.marginLeft ?? false;
        let button;

        if (option.hasSelectMark) {
            button = (
                <SelectMarkButton
                    text={option.text}
                    isFocused={index === verticalPosition}
                    isSelected={option.isSelected}
                    id={index}
                />
            );
        } else {
            button = (
                <Button
                    text={option.text}
                    isFocused={index === verticalPosition}
                    id={index}
                    displayValue={option.displayValue}
                    isLargeSizeButton={isLargeSizeOverlay}
                />
            );
        }

        return (
            <Mousetrap value={index} key={index} className={hasLeftMargin ? 'left-margin' : ''}>
                {button}
            </Mousetrap>
        );
    };

    // buttons division.
    const buttonsContent: Array<JSX.Element> = [];
    if (uiProps.options) {
        uiProps.options.forEach((option, index) => {
            if (option.hrTop)
                buttonsContent.push(
                    <Seperator
                        key={`top-${index}`}
                        type={option.separatorType || SeparatorTypes.OPTIONS}
                    />,
                );
            if (option.horizontalSelectionoptions)
                buttonsContent.push(selectOptionsDiv(index, option));
            else
                buttonsContent.push(
                    selectMarkDiv(index, option, uiProps.isLargeSizeOverlay || false),
                );
            if (option.hrBottom)
                buttonsContent.push(
                    <Seperator
                        key={`bottom-${index}`}
                        type={option.separatorType || SeparatorTypes.OPTIONS}
                    />,
                );
        });
    }

    if (uiProps.overlayType === OverlayTypes.PC_PIN && uiProps.maxInputLength) {
        buttonsContent.push(<PinEntryWidget key="pin-entry" maxLength={uiProps.maxInputLength} />);
    }

    const buttonsDiv = (
        <div className="overlay-buttons-scrollable-wrapper" ref={scrollContainerRef}>
            {buttonsContent}
        </div>
    );

    // dimmer div to be used behind overlays other than shown on playback screen.
    const dimmerClass = uiProps.hasDimmer ? 'dimmer' : 'no-dimmer';
    const dimmerDiv = isOverlayDialogActive ? (
        <Mousetrap
            className={dimmerClass}
            value={uiProps.mouseTrapValue}
            style={{ '--dimmer-opacity': uiProps.dimmerOpacity ?? 0.75 }}
        >
            <div className={dimmerClass} />
        </Mousetrap>
    ) : null;

    // seperator div
    const seperatorDiv = uiProps.hasSeperator ? <Seperator /> : null;

    useEffect(() => {
        if (showOverlay.current) {
            const overlayHeight = overlayRef.current?.clientHeight;
            const descRef = textViewRefs.current.get(0);
            if (
                overlayHeight &&
                overlayHeight > DimensionScaler.getInstance().getScaledHeight(1080, true) &&
                descRef
            ) {
                let newHeight =
                    descRef.clientHeight -
                    (overlayHeight - DimensionScaler.getInstance().getScaledHeight(1080, true));
                // % 44 as the height of each line is 44px
                newHeight -= Number(
                    newHeight % DimensionScaler.getInstance().getScaledHeight(44, true),
                );
                descRef.style.height = `${newHeight}px`;
                descRef.style.overflow = 'hidden';

                // Add transparent effect
                const transparentEffectDiv = document.createElement('div');
                transparentEffectDiv.className = 'transparent-effect';
                descRef.appendChild(transparentEffectDiv);
            }
        } else {
            textViewRefs.current = new Map();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showOverlay.current]);

    // Handle scrolling focused button into view, but only when overlayAnimationComplete is true
    useEffect(() => {
        if (!overlayAnimationComplete.current) {
            return;
        }
        // Overlay animation is complete, safe to scroll immediately
        const buttonElements = scrollContainerRef.current?.children;
        if (!buttonElements || !(buttonElements[verticalPosition] instanceof HTMLElement)) {
            return;
        }

        const element = buttonElements[verticalPosition] as HTMLElement;
        element.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
        });
    }, [verticalPosition, overlayAnimationComplete]);

    const clientHeight = scrollContainerRef.current?.clientHeight || 0;
    const scrollHeight = scrollContainerRef.current?.scrollHeight || 0;
    const isScrollable = scrollContainerRef.current && clientHeight < scrollHeight;
    if (isScrollable && DeviceModel.isWebOsPlatform() && showVerticalCaret) {
        scrollContainerRef.current.classList.add('cursor-scrollable');
    }

    return (
        <div
            className={`overlay-dialog-wrapper ${
                !showOverlay.current ? 'overlay-dialog-hidden' : ''
            }`}
        >
            {dimmerDiv}
            <div
                className={`overlay-dialog-container ${
                    isOverlayDialogActive ? 'show-dialog ' : 'hide-dialog'
                }`}
                data-testid="overlay-dialog-container"
                ref={overlayRef}
            >
                <div className="overlay-header">
                    <div className="row-data">
                        <TextView type={TextViewClasses.TITLE} text={uiProps.title} />
                        {messageCodeDiv}
                    </div>
                    <div className="row-data" data-testid="overlay-title">
                        {subtitileDiv}
                        {brandingIconDiv}
                    </div>
                </div>
                <div className="overlay-body">
                    <RestrictionMessage
                        displayType={RecordingRestriction.OVERLAY_DIALOG}
                        channel={uiProps.channel}
                        programCell={uiProps.programCell}
                    />
                    {descriptionDiv}
                    {seperatorDiv}
                    <div className="overlay-user-options-container" ref={userOptionContainerRef}>
                        {showVerticalCaret && isScrollable ? (
                            <Caret
                                key={`overlay-vertical-caret-north`}
                                className="overlay-vertical-scroll-caret north-caret"
                                direction={InteractiveComponents.NORTH.toLocaleLowerCase()}
                                visible={true}
                                active={activeComponent === InteractiveComponents.NORTH}
                            />
                        ) : null}
                        {buttonsDiv}
                        {showVerticalCaret && isScrollable ? (
                            <Caret
                                key={`overlay-vertical-caret-south`}
                                className="overlay-vertical-scroll-caret south-caret"
                                direction={InteractiveComponents.SOUTH.toLocaleLowerCase()}
                                visible={true}
                                active={activeComponent === InteractiveComponents.SOUTH}
                            />
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OverlayDialog;
